import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaCreditCard, FaShoppingCart } from 'react-icons/fa';
import apiClient from '../lib/api/apiClient';
import { useModernToast } from './ModernToast';
import type { ShopItem } from '../store/shoppingCartStore';
import { useUserStore } from '../store/userStore';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useFranchiseTagSalaries } from '../hooks/useFranchiseTagSalaries';
  
export interface QuickPurchaseResult {
  clientSecret?: string;
  checkoutUrl?: string;
  paymentIntentId?: string;
  amount: number;
  currency: string;
  itemId: string;
}

export interface QuickPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ShopItem | null;
  onPaymentIntentCreated?: (result: QuickPurchaseResult) => void;
}

const QuickPurchaseModal: React.FC<QuickPurchaseModalProps> = ({
  isOpen,
  onClose,
  item,
  onPaymentIntentCreated,
}) => {
  const toast = useModernToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fetchedItem, setFetchedItem] = useState<any>(null);
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const accentColor = useColorModeValue('green.500', 'green.300');
  const cardBackground = useColorModeValue('gray.50', 'gray.700');
  const errorBackground = useColorModeValue('red.50', 'red.900');
  const errorTextColor = useColorModeValue('red.600', 'red.200');
  const { user } = useUserStore();
  const { myLeague } = useMyLeagueStore();
  const { getSalaryByPosition } = useFranchiseTagSalaries();
  
  useEffect(() => {
    if (isOpen) {
      setErrorMessage(null);
      const fetchItem = async () => {
        const response = await apiClient.get(`/store/items/1`);
        const item = response.data as ShopItem;
        setFetchedItem(item);
      };
      fetchItem();
    }
  }, [isOpen]);

  const currency = fetchedItem?.currency ?? 'USD';

  const subtotal = useMemo(() => {
    if (!fetchedItem) {
      return 0;
    }
    return Math.max(fetchedItem.price_cents / 100, 0);
  }, [fetchedItem]);

  const formatter = useMemo(() => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      });
    } catch (intlError) {
      console.warn('QuickPurchaseModal: Unable to create currency formatter.', intlError);
      return null;
    }
  }, [currency]);

  const formatPrice = (value: number) => {
    if (!formatter) {
      return `$${value.toFixed(2)}`;
    }
    return formatter.format(value);
  };

  // Get player position from metadata and calculate release salary
  const playerPosition = useMemo(() => {
    const positionFromMetadata = item?.metadata?.playerPosition as string | undefined;
    const positionFromFetched = fetchedItem?.item?.metadata?.playerPosition as string | undefined;
    return positionFromMetadata || positionFromFetched || null;
  }, [item?.metadata, fetchedItem?.item?.metadata]);

  const releaseSalary = useMemo(() => {
    if (!playerPosition) {
      return null;
    }
    return getSalaryByPosition(playerPosition);
  }, [playerPosition, getSalaryByPosition]);

  // Format release salary for display
  const formattedReleaseSalary = useMemo(() => {
    if (!releaseSalary) {
      return null;
    }
    // API returns salary in dollars, not cents
    return formatPrice(releaseSalary);
  }, [releaseSalary, formatPrice]);

  const handleCreatePaymentIntent = async () => {
    if (!fetchedItem) {
      return;
    }
    //we must first call api /store/items/can-purchase-franchise-tag
    const canPurchaseResponse = await apiClient.get(`/store/items/can-purchase-franchise-tag`);
    console.log('canPurchaseResponse', canPurchaseResponse);
    if (!canPurchaseResponse.data.success) {
      setErrorMessage(canPurchaseResponse.data.error || 'You cannot purchase a franchise tag at this time.');
      return;
    }

    if (subtotal <= 0) {
      setErrorMessage('Unable to start checkout without a valid total.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const amountInMinorUnits = Math.round(fetchedItem.price_cents); // Reason: Stripe expects integer minor units.

    try {
      const originalMetadata = (item?.metadata ?? {}) as Record<string, unknown>;
      const fetchedMetadata = (fetchedItem.item?.metadata ?? {}) as Record<string, unknown>;
      const playerIdFromMetadata =
        (originalMetadata.playerId as string | number | undefined) ??
        (originalMetadata.player_id as string | number | undefined);

      const metadata: Record<string, unknown> = {
        ...fetchedMetadata,
        ...originalMetadata,
        playerId: playerIdFromMetadata ?? item?.id,
        leagueId: myLeague?.id,
      };
      console.log('metadata', metadata);
      //add the current user's id to the metadata
      metadata.userId = user?.id;

      const payload = {
        items: [
          {
            id: fetchedItem.id,
            name: fetchedItem.item.name_en,
            amount: amountInMinorUnits,
            currency,
            metadata,
          },
        ],
        // Reason: Allow future support for passing arbitrary metadata without changing the API signature.
        quickPurchase: true,
      };

      const response = await apiClient.post('/payment/create-payment-intent', payload);
      console.log(response.data);
      const { clientSecret, checkoutUrl, paymentIntentId } = response.data ?? {};

      if (!clientSecret && !checkoutUrl) {
        throw new Error('Stripe details were not returned by the server.');
      }

      onPaymentIntentCreated?.({
        clientSecret,
        checkoutUrl,
        paymentIntentId,
        amount: subtotal,
        currency,
        itemId: fetchedItem.id,
      });

      toast.success('Checkout Ready', 'Redirecting you to finish the quick purchase.');
      onClose();
    } catch (error) {
      const fallbackMessage = 'We could not start the checkout. Please try again.';
      const axiosMessage =
        (error as { response?: { data?: { error?: string; message?: string } } }).response?.data?.error ??
        (error as { response?: { data?: { error?: string; message?: string } } }).response?.data?.message;
      const message = error instanceof Error ? error.message : axiosMessage ?? fallbackMessage;
      setErrorMessage(message);
      toast.error('Payment Error', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!fetchedItem) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="2xl" boxShadow="2xl">
        <ModalHeader borderBottom="1px solid" borderColor={borderColor} pb={4}>
          <HStack spacing={3}>
            <Icon as={FaShoppingCart} color={accentColor} boxSize={5} />
            <Text fontSize="lg" fontWeight="bold" color={textColor}>
              Franchise Tag
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={6}>
          <VStack align="stretch" spacing={5}>
            <Box p={4} borderRadius="lg" border="1px solid" borderColor={borderColor} bg={cardBackground}>
              <VStack align="stretch" spacing={2}>
                <HStack justify="space-between" align="center">
                  <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                    {item?.description}
                  </Text>
                  {/* <Badge bg={badgeBg} color={badgeColor} borderRadius="full" px={3} py={1}>
                    {fetchedItem.category}
                  </Badge> */}
                </HStack>
                {fetchedItem.description && (
                  <Text fontSize="sm" color={secondaryTextColor} lineHeight="tall">
                    {fetchedItem.item.description_en}
                  </Text>
                )}
                {formattedReleaseSalary && (
                  <Text fontSize="sm" color={secondaryTextColor} lineHeight="tall" mt={2}>
                    Contract value with franchise tag: <Text as="span" fontWeight="semibold" color={textColor}>{formattedReleaseSalary}</Text>
                  </Text>
                )}
                <Text fontSize="sm" color={secondaryTextColor} lineHeight="tall" mt={2}>
                Only one Franchise Tag may be purchased per season.
                </Text>
              </VStack>
            </Box>

            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <Text fontSize="sm" color={secondaryTextColor}>
                  Unit Price
                </Text>
                <Text fontWeight="medium" color={textColor}>
                  {formatPrice(subtotal)}
                </Text>
              </HStack>
              <HStack justify="space-between">
                <Text fontSize="lg" fontWeight="semibold" color={textColor}>
                  Total
                </Text>
                <Text fontSize="xl" fontWeight="bold" color={accentColor}>
                  {formatPrice(subtotal)}
                </Text>
              </HStack>
            </VStack>

            {errorMessage && (
              <Box borderRadius="md" border="1px solid" borderColor="red.400" bg={errorBackground} color={errorTextColor} p={3}>
                <Text fontSize="sm">{errorMessage}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor={borderColor} pt={4} gap={3}>
          <Button variant="outline" onClick={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleCreatePaymentIntent}
            isLoading={isSubmitting}
            leftIcon={<FaCreditCard />}
          >
            Continue to Checkout
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default QuickPurchaseModal;

